/**
 * Colore e sigla del canale (novita N10 del redesign v4, decisione Q6):
 * derivati in modo deterministico dal canale, nessun campo nel modello.
 *
 * Palette dal design (`Centro Ticket v4.dc.html` L790-L797). Tre toni del
 * prototipo NON reggono il testo bianco in AA (4.5:1) e sono stati scuriti
 * del minimo indispensabile mantenendo la tinta:
 * `#0A8F4B`→`#098647`, `#1D7FC0`→`#1C7AB8`, `#7A861A`→`#707B18`.
 * I tre toni aggiunti per arrivare a 8 vengono dagli avatar del design (L797).
 *
 * Varianti (precalcolate, niente `color-mix` a runtime):
 * - `fgDark`  = mix 55% col bianco (L799), per testo/tratti colorati su fondo scuro;
 * - `softLight`/`softDark` = tinta al 9% / 18% (L800);
 * - `hero`    = mix 74% con `#04140B` (L1005), fondo dell'intestazione del ticket.
 *
 * I valori sono gli stessi di `styles/support-v4/tokens.css` (`--s4-ch-*`):
 * un test verifica che i due restino allineati.
 */

export type ChannelToneKey = 'def' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export interface ChannelSwatch {
  key: ChannelToneKey;
  /** Fondo pieno (sigla, swatch, rail): testo bianco sopra in AA. */
  color: string;
  fgDark: string;
  hero: string;
}

export interface ChannelTone extends ChannelSwatch {
  /** Sigla mono di massimo 3 lettere (es. «SEG»). */
  sigla: string;
  softLight: string;
  softDark: string;
  /** Nome della variabile CSS del tono (es. `--s4-ch-3`, `--s4-ch-def`). */
  cssVar: string;
}

export const CHANNEL_DEFAULT_SWATCH: ChannelSwatch = { key: 'def', color: '#5E6B64', fgDark: '#A6AEAA', hero: '#47544D' };

export const CHANNEL_PALETTE: readonly ChannelSwatch[] = [
  { key: 1, color: '#098647', fgDark: '#78BC9A', hero: '#086837' },
  { key: 2, color: '#4B58D1', fgDark: '#9CA3E6', hero: '#39469E' },
  { key: 3, color: '#B03C7E', fgDark: '#D494B8', hero: '#833260' },
  { key: 4, color: '#1C7AB8', fgDark: '#82B6D8', hero: '#165F8B' },
  { key: 5, color: '#707B18', fgDark: '#B0B680', hero: '#546015' },
  { key: 6, color: '#8A5A2B', fgDark: '#BFA48A', hero: '#674823' },
  { key: 7, color: '#6A4A9C', fgDark: '#AD9BC9', hero: '#4F3C76' },
  { key: 8, color: '#26707F', fgDark: '#88B0B9', hero: '#1D5861' },
];

export interface ChannelLike {
  _id?: string | null;
  nome?: string | null;
  inboundDefault?: boolean | null;
  /** Avatar del canale (emoji/immagine): non cambia colore ne sigla, li usa `ChannelSwatch`. */
  avatar?: { kind?: string | null; emoji?: string | null; url?: string | null } | null;
}

// FNV-1a 32 bit: stabile fra sessioni e browser, nessuna dipendenza.
const fnv1a = (s: string): number => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
};

const STOPWORDS = new Set([
  'e', 'ed', 'o', 'di', 'del', 'dello', 'della', 'dei', 'degli', 'delle', 'il', 'lo', 'la', 'i', 'gli', 'le',
  'l', 'un', 'una', 'a', 'al', 'alla', 'ai', 'agli', 'alle', 'da', 'dal', 'in', 'nel', 'nella', 'su', 'per', 'con',
]);

/** Sigla del canale: prime 3 lettere della prima parola significativa, maiuscole, senza accenti. */
export const channelSigla = (nome?: string | null): string => {
  const words = (nome || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
  const word = words.find((w) => !STOPWORDS.has(w.toLowerCase())) || words[0];
  return word ? word.slice(0, 3).toUpperCase() : '?';
};

const hexToRgb = (hex: string): [number, number, number] => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];

const rgba = (hex: string, alpha: number): string => {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** Indice 1-8 della palette per il canale (stesso canale → stesso indice). */
export const channelPaletteIndex = (ch: ChannelLike): number => {
  const seed = ch._id || ch.nome || '';
  return (fnv1a(seed) % CHANNEL_PALETTE.length) + 1;
};

/**
 * Tono completo del canale. Il canale predefinito (`inboundDefault`) prende il
 * grigio-verde neutro del design; gli altri un tono della palette dal loro `_id`
 * (in mancanza, dal nome).
 */
export const channelTone = (ch?: ChannelLike | null): ChannelTone => {
  const c = ch || {};
  const swatch = c.inboundDefault ? CHANNEL_DEFAULT_SWATCH : CHANNEL_PALETTE[channelPaletteIndex(c) - 1];
  return {
    ...swatch,
    sigla: channelSigla(c.nome),
    softLight: rgba(swatch.color, 0.09),
    softDark: rgba(swatch.color, 0.18),
    cssVar: `--s4-ch-${swatch.key}`,
  };
};

// ── Contrasto WCAG 2.x (usato dai test e disponibile ai componenti) ──
const channel = (v: number): number => {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

export const relativeLuminance = (hex: string): number => {
  const [r, g, b] = hexToRgb(hex).map(channel);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const contrastRatio = (a: string, b: string): number => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};
